"use client";

import type { JSX } from "react";
import { InputFileUpload, Loader, Text, View } from "@/components";
import { useFileUpload } from "@/hooks/elearning";
import { useLayout, useNotification } from "@/providers";

export interface FileUploadFieldProps {
  id: string;
  name: string;
  /** Tipos aceptados (p. ej. "application/pdf" o "image/png,image/jpeg,image/webp"). */
  accept: string;
  label?: string;
  className?: string;
  /** Recibe la URL interna (`/v2/files/{id}`) al completarse la subida. */
  onUploaded: (url: string) => void;
  /**
   * URL actualmente guardada en el campo dueño. Si se pasa, la nota inline de
   * "subido" se muestra solo mientras el campo siga referenciando el archivo
   * (p. ej. desaparece al tocar "Eliminar imagen").
   */
  value?: string;
}

/**
 * @name FileUploadField
 * @description Campo de subida de archivos al módulo files del backend
 * (upload-only: reemplaza a los inputs de URL manual). Compone la molécula
 * `InputFileUpload` (drag & drop, validación de `accept`) con `useFileUpload`
 * (proxy autenticado `/api/files`): al seleccionar un archivo lo sube
 * automáticamente y entrega la URL interna por `onUploaded`, para que el
 * dueño la persista con su propio submit (PUT del documento del curso,
 * POST/PATCH de la pregunta). Muestra estado inline (subiendo / subido /
 * clave de error) y notifica el resultado.
 *
 * @param {FileUploadFieldProps} props - Props del componente.
 * @returns {JSX.Element} El campo de subida.
 */
const FileUploadField = ({
  id,
  name,
  accept,
  label,
  className = "",
  onUploaded,
  value
}: FileUploadFieldProps): JSX.Element => {
  const { t } = useLayout();
  const { notify } = useNotification();
  const { upload, uploading, errorKey, file } = useFileUpload();

  // Con `value`, la nota de subido acompaña al campo dueño: si este dejó de
  // referenciar el archivo (p. ej. lo quitó), la nota deja de mostrarse.
  const uploadedVigente = file && (value === undefined || value === file.url);

  const handleFile = async (selected: File | null) => {
    // null = el usuario quitó la selección local del widget; no hay nada que subir.
    if (!selected || uploading) {
      return;
    }
    const result = await upload(selected);
    if (result.file) {
      onUploaded(result.file.url);
      notify("success", t("fileUploaded"));
      return;
    }
    if (result.errorKey) {
      notify("warning", t(result.errorKey));
    }
  };

  return (
    <View className={className}>
      <InputFileUpload
        id={id}
        name={name}
        label={label}
        accept={accept}
        onUpload={handleFile}
        resetAfterUpload
      />
      {uploading && (
        <View className="flex items-center gap-2 mt-2">
          <Loader className="h-4 w-4" />
          <Text className="text-xs text-muted-foreground">
            {t("uploading")}
          </Text>
        </View>
      )}
      {!uploading && !errorKey && uploadedVigente && (
        <Text className="text-xs text-primary mt-2">
          {`${file.filename} · ${t("fileUploaded")}`}
        </Text>
      )}
      {!uploading && errorKey && (
        <Text className="text-xs text-destructive mt-2">{t(errorKey)}</Text>
      )}
    </View>
  );
};

export default FileUploadField;
